import { Injectable } from '@angular/core';
import { LocationService } from '@core/jss-seo';
import type { Gender } from '@essent/common';
import {
  emobilityActions,
  getAccountHolderString,
  paymentDetailsFormId,
} from '@innogy/emobility-shared';
import { getHasChargeCards } from '@innogy/emobility/chargecard/store';
import { isNotNullish } from '@innogy/utils-rxjs';
import {
  ChargePointType,
  LeadFormPaymentDetails,
  ChargingStationColor,
  OrganizationDetails,
  ProductColor,
  ProductSelection,
  type EmobilityChargeStationDetails,
  ChargingStationType,
} from '@integration/api-client/emobility';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import {
  filter,
  mergeMap,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import {
  ResetFormStepAction,
  SubmitFormStepAction,
} from '@innogy/progressive-ngrx-forms';

import {
  chargePointConfigurationFormId,
  type ChargePointConfigurationFormState,
} from './lead-form-charge-point-configuration.reducer';
import type { CustomerDetailsFormState } from './lead-form-customer-details.reducer';
import {
  customerDetailsFormId,
  CustomerType,
  CustomerTypeSegmentTypeMap,
} from './lead-form-customer-details.reducer';
import { installationAddressFormId } from './lead-form.address.reducer';
import { leadConsentsFormId } from './lead-form.consents.reducer';
import {
  getProposition,
  getPropositionType,
  selectCampaignId,
  selectChargePointConfigurationValue,
  selectCustomerDetailsValue,
  selectLeadFormAddressFormValue,
  selectLeadFormThankYouPage,
  selectLeadFormConsentsValue,
  selectLeadFormPaymentDetailsValue,
  selectLeadFormTrackingId,
  selectProspectVerificationCode,
  selectSalesPartnerLeadData,
  isEmobilityIbanVerificationEnabled,
  selectLeadFormRetryPage,
  selectLeadFormPaymentDetailsDisabled,
  selectEmobilityCustomerSegment,
} from './lead-form.selectors';
import { chargeCardTypeFormId } from './lead-form-charge-card-type.reducer';
import type { LeadFormsPaymentDetailsFormValues } from './lead-form-payment-details.reducer';

@Injectable()
export class LeadFormSubmitEffects {
  leadFormProposition$ = this.store$
    .select(getProposition)
    .pipe(filter(isNotNullish));

  leadFormSelectedProduct$ = this.store$.select(
    selectChargePointConfigurationValue
  );
  leadFormCustomerDetails$ = this.store$.select(selectCustomerDetailsValue);
  leadFormAddress$ = this.store$.select(selectLeadFormAddressFormValue);
  leadFormPaymentDetails$ = this.store$.select(
    selectLeadFormPaymentDetailsValue
  );
  leadFormConsents$ = this.store$.select(selectLeadFormConsentsValue);
  leadFormThankYouPage$ = this.store$.select(selectLeadFormThankYouPage);
  leadFormRetryPage$ = this.store$.select(selectLeadFormRetryPage);
  customerSegment$ = this.store$.select(selectEmobilityCustomerSegment);
  propositionType$ = this.store$.select(getPropositionType);
  campaignId$ = this.store$.select(selectCampaignId);
  salesPartnerLeadData$ = this.store$.select(selectSalesPartnerLeadData);
  verificationCode$ = this.store$.select(selectProspectVerificationCode);
  e2eTrackingId$ = this.store$.select(selectLeadFormTrackingId);
  leadFormPaymentDetailsDisabled$ = this.store$.select(
    selectLeadFormPaymentDetailsDisabled
  );
  isIbanVerificationEnabled$ = this.store$.select(
    isEmobilityIbanVerificationEnabled
  );

  // a customer can't have charge cards if they are not logged in
  hasChargeCards$ = this.customerSegment$.pipe(
    mergeMap((customerData) =>
      customerData ? this.store$.select(getHasChargeCards) : of(false)
    )
  );

  constructor(
    private readonly actions$: Actions,
    private readonly store$: Store<any>,
    private readonly locationService: LocationService
  ) {}

  public readonly submitLeadForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType<SubmitFormStepAction>(SubmitFormStepAction.TYPE),
      filter((action) => action.stepId === leadConsentsFormId),
      concatLatestFrom(() => [
        this.leadFormCustomerDetails$,
        this.leadFormAddress$,
        this.leadFormConsents$,
        this.leadFormThankYouPage$,
        this.leadFormRetryPage$,
        this.leadFormProposition$,
        this.leadFormSelectedProduct$,
        this.leadFormPaymentDetails$,
        this.customerSegment$,
        this.propositionType$,
        this.hasChargeCards$,
        this.campaignId$,
        this.salesPartnerLeadData$,
        this.verificationCode$,
        this.e2eTrackingId$,
        this.leadFormPaymentDetailsDisabled$,
      ]),
      filter(
        ([_, customerDetails]) =>
          customerDetails.customerType !== CustomerType.SME
      ),
      switchMap(
        ([
          _,
          customerDetails,
          address,
          consents,
          thankYouPage,
          retryPage,
          proposition,
          selectedProduct,
          paymentDetails,
          customerSegment,
          propositionType,
          hasChargeCards,
          campaignId,
          salesPartnerLeadData,
          verificationCode,
          e2eTrackingId,
          paymentDetailsDisabled,
        ]) => [
          emobilityActions.postLeadV1.requestAction({
            payload: {
              requestBody: {
                verification_code: verificationCode,
                e_2_e_tracking_id: e2eTrackingId,
                proposition,
                campaign_id: campaignId,
                personal_details: {
                  salutation: customerDetails.salutation as Gender,
                  initials: customerDetails.initials,
                  prefix: customerDetails.prefix,
                  last_name: customerDetails.lastName,
                  date_of_birth: customerDetails.dateOfBirth,
                  phone_number: getPhoneNumber(customerDetails),
                  email_address: getEmailAddress(customerDetails),
                },
                organization_details:
                  this.getOrganizationDetails(customerDetails),
                payment_details: paymentDetailsDisabled
                  ? undefined
                  : this.getPaymentDetails(
                      propositionType,
                      customerDetails,
                      paymentDetails
                    ),
                installation_address: {
                  postal_code: address.installationAddressState.postalCode,
                  house_number:
                    address.installationAddressState.communicationNumber,
                  house_number_addition:
                    address.installationAddressState.houseNumberAddition,
                  street: address.installationAddressState.street,
                  city: address.installationAddressState.city,
                },
                consents: {
                  acceptedCallFromAdvisor: consents.acceptedCallFromAdvisor,
                  acceptedPaymentConditions: consents.acceptedPaymentConditions,
                  acceptedTermsAndConditions:
                    consents.acceptedTermsAndConditions,
                  acceptedTariffRateChange: consents.acceptedTariffRateChange,
                  acceptedProductMail: consents.acceptedProductMail,
                },
                correspondence_address: address.divergentCorrespondenceAddress
                  ? {
                      postal_code:
                        address.correspondenceAddressState.postalCode,
                      house_number:
                        address.correspondenceAddressState.communicationNumber,
                      house_number_addition:
                        address.correspondenceAddressState.houseNumberAddition,
                      street: address.correspondenceAddressState.street,
                      city: address.correspondenceAddressState.city,
                    }
                  : undefined,
                customer_information: {
                  hasChargeCard: hasChargeCards,
                  currentSegmentType: customerSegment,
                  requestedSegmentType:
                    CustomerTypeSegmentTypeMap[customerDetails.customerType],
                },
                charge_station_details:
                  this.getChargeStationDetails(selectedProduct),
                external_partner_id: salesPartnerLeadData.externalPartnerId,
                request_context: salesPartnerLeadData.data,
                callback_moment: consents.callbackMoment,
                return_url: thankYouPage?.href,
                retry_url: retryPage?.href,
              },
            },
          }),
        ]
      )
    )
  );

  public readonly onSuccessLeadFormSubmit$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(emobilityActions.postLeadV1.successAction),
        withLatestFrom(
          this.leadFormThankYouPage$,
          this.isIbanVerificationEnabled$
        ),
        tap(([action, thankYouPage, isIbanVerificationEnabled]) => {
          if (
            action.payload.iban_verification_url &&
            isIbanVerificationEnabled
          ) {
            this.locationService.navigateGeneralLink(
              action.payload.iban_verification_url
            );
          } else if (thankYouPage?.href) {
            this.locationService.navigateGeneralLink(thankYouPage.href);
          }
        })
      ),
    { dispatch: false }
  );

  public readonly onCreateIbanVerificationSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(emobilityActions.createIbanVerificationPaymentV1.successAction),
        tap((action) => {
          if (action.payload.paymentUrl) {
            this.locationService.navigateGeneralLink(action.payload.paymentUrl);
          }
        })
      ),
    { dispatch: false }
  );

  public readonly onSuccessSMELeadFormSubmit$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(emobilityActions.postSmeLeadFormV1.successAction),
        withLatestFrom(this.leadFormThankYouPage$),
        tap(([_, thankYouPage]) => {
          if (thankYouPage?.href) {
            this.locationService.navigateGeneralLink(thankYouPage.href);
          }
        })
      ),
    { dispatch: false }
  );

  public readonly clearLeadFormOnSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        emobilityActions.postLeadV1.successAction,
        emobilityActions.postSmeLeadFormV1.successAction
      ),
      mergeMap(() => [
        new ResetFormStepAction(chargePointConfigurationFormId),
        new ResetFormStepAction(chargeCardTypeFormId),
        new ResetFormStepAction(leadConsentsFormId),
        new ResetFormStepAction(customerDetailsFormId),
        new ResetFormStepAction(installationAddressFormId),
        new ResetFormStepAction(paymentDetailsFormId),
      ])
    )
  );

  private getPaymentDetails(
    proposition: ProductSelection | undefined,
    customerDetails: CustomerDetailsFormState,
    paymentDetails: LeadFormsPaymentDetailsFormValues
  ): LeadFormPaymentDetails | undefined {
    if (proposition !== ProductSelection.CHARGE_CARD) {
      return undefined;
    }

    switch (customerDetails.customerType) {
      case CustomerType.SelfEmployed:
        return {
          account_holder: customerDetails.businessDetails.organizationName,
          iban: paymentDetails.iban,
        };
      case CustomerType.Household:
        return {
          account_holder: getAccountHolderString(
            customerDetails.initials,
            customerDetails.prefix,
            customerDetails.lastName
          ),
          iban: paymentDetails.iban,
        };
      default:
        return undefined;
    }
  }

  private getOrganizationDetails(
    customerDetails: CustomerDetailsFormState
  ): OrganizationDetails | undefined {
    if (customerDetails.customerType === CustomerType.SelfEmployed) {
      return {
        chamber_of_commerce_number:
          customerDetails.businessDetails.chamberOfCommerceNumber,
        organization_name: customerDetails.businessDetails.organizationName,
      };
    }

    return undefined;
  }

  private getChargeStationDetails(
    formState: ChargePointConfigurationFormState
  ): EmobilityChargeStationDetails | undefined {
    return {
      color: formState.productColor
        ? ProductColorMap[formState.productColor]
        : undefined,
      fixedCable: formState.fixedCable,
      type: formState.chargePointType
        ? ChargePointTypeMap[formState.chargePointType]
        : undefined,
      mountType: formState.mountType,
      interestedInReimbursement: formState.interestedInReimbursement,
    };
  }
}

export function getEmailAddress(formState: CustomerDetailsFormState) {
  return formState.customerType === CustomerType.Household
    ? formState.emailAddress
    : formState.businessDetails.emailAddress;
}

export function getPhoneNumber(formState: CustomerDetailsFormState) {
  return formState.customerType === CustomerType.Household
    ? formState.phoneNumber
    : formState.businessDetails.phoneNumber;
}

const ProductColorMap = {
  [ProductColor.Antraciet]: ChargingStationColor.Anthracite,
  [ProductColor.Wit]: ChargingStationColor.White,
};

const ChargePointTypeMap = {
  [ChargePointType.ProLine]: ChargingStationType.ProLine,
  [ChargePointType.SLine]: ChargingStationType.SLine,
};
